package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ClientsViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.ClubClientDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.modal.ClientDetailPopup
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.components.widgets.SortDirection
import com.arena_web.ui.components.widgets.TableSortDual
import com.arena_web.ui.layouts.BusinessPageLayout
import com.arena_web.ui.layouts.LocalSelectedClubId
import com.arena_web.ui.localization.LocalAppStrings
import com.varabyte.kobweb.core.rememberPageContext
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input

@Page("/clients")
@Composable
fun ClientsPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val clubAdminRepo = container.clubAdminRepository

    val viewModel = if (clubAdminRepo != null) rememberViewModel {
        ClientsViewModel(clubAdminRepo)
    } else null

    val state = viewModel?.uiState?.collectAsState()?.value

    BusinessPageLayout(
        activeTab = BusinessSidebarTab.Clients
    ) {
        if (viewModel == null || state == null) return@BusinessPageLayout

        val layoutClubId = LocalSelectedClubId.current
        LaunchedEffect(layoutClubId) {
            if (layoutClubId != null && layoutClubId != state.clubId) {
                viewModel.switchClub(layoutClubId)
            }
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(30, 40)
                .sGap(24)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            // Main container with gradient background
            Div(attrs = {
                style {
                    width(100.percent)
                    property("flex", "1")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("border", "1px solid rgba(41, 52, 138, 0.2)")
                    property("border-radius", s(18))
                    property("background", "linear-gradient(178deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)")
                    property("min-height", "0")
                }
            }) {
                // Header section
                ClientsHeader(
                    total = state.total,
                    activeCount = state.activeCount,
                    totalBookings = state.totalBookings,
                    totalRevenue = state.totalRevenue,
                    search = state.search,
                    statusFilter = state.statusFilter,
                    onSearchChanged = { viewModel.onSearchChanged(it) },
                    onStatusFilterChanged = { viewModel.onStatusFilterChanged(it) },
                )

                // Table
                ClientsTable(
                    clients = state.clients,
                    sortBy = state.sortBy,
                    sortAscending = state.sortAscending,
                    onSortChanged = { col, dir -> viewModel.onSortChanged(col, dir) },
                    onClientClick = { viewModel.openClientDetail(it) },
                )

                // Pagination
                ClientsPagination(
                    currentPage = state.page,
                    totalPages = viewModel.totalPages,
                    onPageChanged = { viewModel.onPageChanged(it) },
                )
            }
        }

        // Client detail popup
        if (state.selectedClientCard != null) {
            ClientDetailPopup(
                card = state.selectedClientCard,
                bookings = state.selectedClientBookings,
                noteText = state.noteText,
                onNoteTextChanged = { viewModel.onNoteTextChanged(it) },
                onAddNote = { viewModel.addNote() },
                onSendMessage = {
                    val client = state.selectedClientCard
                    viewModel.closeClientDetail()
                    val encode: (String) -> String = { js("encodeURIComponent")(it) as String }
                    val params = buildString {
                        append("/chat?user=${client.userId}")
                        client.fullName?.let { append("&name=${encode(it)}") }
                        client.avatarUrl?.let { append("&avatar=${encode(it)}") }
                    }
                    ctx.router.navigateTo(params)
                },
                onClose = { viewModel.closeClientDetail() },
            )
        }
    }
}

// =============================================================================
// Header: Title + Metrics + Search + Filters + Add button
// =============================================================================

@Composable
private fun ClientsHeader(
    total: Int,
    activeCount: Int,
    totalBookings: Long,
    totalRevenue: String,
    search: String,
    statusFilter: String?,
    onSearchChanged: (String) -> Unit,
    onStatusFilterChanged: (String?) -> Unit,
) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            width(100.percent)
            property("padding", "${s(16)} ${s(12)}")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", s(24))
        }
    }) {
        // Top row: Title + Metrics | Search + Filter + Add
        Div(attrs = {
            style {
                width(100.percent)
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(24))
            }
        }) {
            // Left: Title + Metrics
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", s(120))
                }
            }) {
                // Title
                Column(modifier = Modifier.sGap(4)) {
                    SpanText(
                        t.pageClientsTitle,
                        modifier = Modifier.h2().color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        t.pageClientsTotal(total),
                        modifier = Modifier.bodyXlRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }

                // Metric cards
                Row(
                    modifier = Modifier.sGap(12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    MetricCard(count = activeCount.toString(), label = t.pageActiveClients)
                    MetricCard(count = totalBookings.toString(), label = t.pageTotalBookings)
                    MetricCard(count = "$ ${formatMoney(totalRevenue)}", label = t.pageTotalRevenue)
                }
            }

            // Right side spacer + controls
            Div(attrs = {
                style {
                    property("flex", "1")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "flex-end")
                    property("gap", s(12))
                }
            }) {
                // Search input
                SearchInput(value = search, onValueChange = onSearchChanged)

                // Status filter
                StatusFilterDropdown(
                    selected = statusFilter,
                    onSelect = onStatusFilterChanged
                )

                // Add client button
                AddClientButton()
            }
        }
    }
}

@Composable
private fun MetricCard(count: String, label: String) {
    Div(attrs = {
        style {
            property("border", "0.3px solid transparent")
            property("border-radius", s(100))
            property("padding", "${s(10)} ${s(18)}")
            property("background",
                "${AppGradients.InteractiveSecondary} padding-box, " +
                "linear-gradient(0deg, #0A142A, #0A142A) padding-box, " +
                "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("min-width", s(151))
            property("display", "flex")
            property("align-items", "center")
            property("gap", s(8))
            property("white-space", "nowrap")
        }
    }) {
        SpanText(
            count,
            modifier = Modifier.businessBodyXlSemiBold().color(AppColors.Primary)
        )
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
        )
    }
}

@Composable
private fun SearchInput(value: String, onValueChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            property("border", "0.5px solid transparent")
            property("border-radius", s(28))
            property("height", s(44))
            property("width", s(343))
            property("display", "flex")
            property("align-items", "center")
            property("gap", s(6))
            property("padding-left", s(12))
            property("padding-right", s(8))
            property("background",
                "${AppGradients.InteractiveSecondary} padding-box, " +
                "linear-gradient(0deg, #0A142A, #0A142A) padding-box, " +
                "${AppGradients.ButtonBorderGradientPrimary} border-box")
        }
    }) {
        Icon(
            src = AppIcons.Search,
            modifier = Modifier.sSize(20).styleModifier {
                property("filter", AppColors.IconDefaultFilter)
                property("opacity", "0.5")
                property("flex-shrink", "0")
            }
        )

        Input(type = InputType.Text) {
            value(value)
            onInput { onValueChange(it.value) }
            attr("placeholder", t.commonSearch)
            style {
                property("flex", "1")
                property("background", "transparent")
                property("border", "none")
                property("outline", "none")
                property("color", "#bed6f5")
                property("font-size", s(16))
                property("font-family", "Urbanist, sans-serif")
                property("font-weight", "400")
                property("line-height", s(18))
            }
        }
    }
}

@Composable
private fun StatusFilterDropdown(selected: String?, onSelect: (String?) -> Unit) {
    val t = LocalAppStrings.current
    var isOpen by remember { mutableStateOf(false) }

    val label = when (selected) {
        "VIP" -> t.pageStatusVip
        "REGULAR" -> t.pageStatusActive
        "DEBTOR" -> t.pageStatusDebtor
        else -> t.pageStatusAll
    }

    Box(modifier = Modifier.position(Position.Relative)) {
        Div(attrs = {
            style {
                property("border", "0.5px solid transparent")
                property("border-radius", s(26))
                property("height", s(44))
                property("width", s(145))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("padding", "0 ${s(18)}")
                property("cursor", "pointer")
                property("background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                    "linear-gradient(0deg, #0A142A, #0A142A) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("user-select", "none")
            }
            onClick { isOpen = !isOpen }
        }) {
            SpanText(
                label,
                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
            )
            Icon(
                src = AppIcons.ChevronDown,
                modifier = Modifier.sSize(12).styleModifier {
                    property("filter", AppColors.IconDefaultFilter)
                }
            )
        }

        // Dropdown list
        if (isOpen) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(100.percent)
                    .left(0.px)
                    .sMarginTop(4)
                    .sWidth(145)
                    .zIndex(100)
                    .sBorderRadius(12)
                    .overflow(Overflow.Hidden)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property("background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                            "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.3)")
                    }
            ) {
                val options = listOf(
                    null to t.pageStatusAll,
                    "REGULAR" to t.pageStatusActive,
                    "VIP" to t.pageStatusVip,
                    "DEBTOR" to t.pageStatusDebtor,
                )
                options.forEach { (value, optionLabel) ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPadding(10, 14)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                onSelect(value)
                                isOpen = false
                            }
                            .styleModifier {
                                if (value == selected) {
                                    property("background", "rgba(0, 97, 255, 0.15)")
                                }
                            }
                    ) {
                        SpanText(
                            optionLabel,
                            modifier = Modifier.bodyLMedium().color(
                                if (value == selected) AppColors.Primary
                                else AppColors.Text.Secondary
                            )
                        )
                    }
                }
            }

            // Click-away overlay
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(99)
                    .onClick { isOpen = false }
            )
        }
    }
}

@Composable
private fun AddClientButton() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "relative")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Circle icon
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(AppColors.Primary)
                .styleModifier {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("z-index", "1")
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.sSize(15).styleModifier {
                    property("filter", "brightness(0) invert(1)")
                }
            )
        }

        // Button label (overlapping the circle)
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(AppColors.Primary)
                .sMarginLeft(-7)
                .styleModifier {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("padding", "0 ${s(24)}")
                },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.pageAddNewClient,
                modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
            )
        }
    }
}

// =============================================================================
// Table
// =============================================================================

@Composable
private fun ClientsTable(
    clients: List<ClubClientDto>,
    sortBy: String,
    sortAscending: Boolean,
    onSortChanged: (String, SortDirection) -> Unit,
    onClientClick: (String) -> Unit,
) {
    var selectedClientId by remember { mutableStateOf<String?>(null) }

    Column(modifier = Modifier.fillMaxWidth().sGap(0)) {
        // Table header
        TableHeader(sortBy = sortBy, sortAscending = sortAscending, onSortChanged = onSortChanged)

        // Table rows
        Div(attrs = {
            style {
                width(100.percent)
                property("display", "flex")
                property("flex-direction", "column")
            }
        }) {
            clients.forEach { client ->
                ClientRow(
                    client = client,
                    isSelected = selectedClientId == client.userId,
                    onSelect = {
                        selectedClientId = client.userId
                        onClientClick(client.userId)
                    }
                )
            }
        }
    }
}

@Composable
private fun TableHeader(sortBy: String, sortAscending: Boolean, onSortChanged: (String, SortDirection) -> Unit) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            width(100.percent)
            property("display", "grid")
            property("grid-template-columns", "2fr 1.2fr 1.2fr 1.5fr 1.2fr 1fr")
            property("align-items", "center")
            property("padding", "${s(16)} ${s(12)}")
            property("background", "rgba(0, 0, 0, 0.3)")
            property("border-left", "1px solid rgba(41, 52, 138, 0.2)")
            property("border-right", "1px solid rgba(41, 52, 138, 0.2)")
        }
    }) {
        SortableHeaderCell(t.pageColName, "name", sortBy, sortAscending, onSortChanged)
        SortableHeaderCell(t.modalBookings, "bookings", sortBy, sortAscending, onSortChanged)
        SortableHeaderCell(t.pageColRevenue, "spend", sortBy, sortAscending, onSortChanged)
        SortableHeaderCell(t.pageColStatus, "status", sortBy, sortAscending, onSortChanged)
        SortableHeaderCell(t.pageColLastVisit, "lastVisit", sortBy, sortAscending, onSortChanged)
        HeaderCell(t.pageColActions)
    }
}

@Composable
private fun SortableHeaderCell(
    title: String,
    column: String,
    currentSort: String,
    sortAscending: Boolean,
    onSortChanged: (String, SortDirection) -> Unit
) {
    val direction = when {
        currentSort != column -> SortDirection.None
        sortAscending -> SortDirection.Ascending
        else -> SortDirection.Descending
    }

    Div(attrs = {
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", s(8))
            property("cursor", "pointer")
            property("user-select", "none")
        }
        onClick {
            // Click on header text: cycle desc → asc → none
            val next = when (direction) {
                SortDirection.None -> SortDirection.Descending
                SortDirection.Descending -> SortDirection.Ascending
                SortDirection.Ascending -> SortDirection.None
            }
            onSortChanged(column, next)
        }
    }) {
        SpanText(
            title,
            modifier = Modifier.h2Medium().color(AppColors.Text.Secondary)
        )

        TableSortDual(
            direction = direction,
            onDirectionChange = { newDir -> onSortChanged(column, newDir) }
        )
    }
}

@Composable
private fun HeaderCell(title: String) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("align-items", "center")
        }
    }) {
        SpanText(
            title,
            modifier = Modifier.h2Medium().color(AppColors.Text.Secondary)
        )
    }
}

// =============================================================================
// Client Row
// =============================================================================

@Composable
private fun ClientRow(
    client: ClubClientDto,
    isSelected: Boolean,
    onSelect: () -> Unit
) {
    var isHovered by remember { mutableStateOf(false) }

    Div(attrs = {
        style {
            width(100.percent)
            property("display", "grid")
            property("grid-template-columns", "2fr 1.2fr 1.2fr 1.5fr 1.2fr 1fr")
            property("align-items", "center")
            property("padding", "${s(4)} ${s(12)} ${s(6)}")
            property("height", s(76))
            property("border-bottom", "1px solid rgba(148, 149, 152, 0.3)")
            property("transition", "background-color 150ms ease")
            property("cursor", "pointer")
            property("background", if (isHovered || isSelected) "#09142d" else "transparent")
        }
        onClick { onSelect() }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
    }) {
        // Name + Avatar + Email
        Div(attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(16))
            }
        }) {
            // Avatar
            Div(attrs = {
                style {
                    property("width", s(44))
                    property("height", s(44))
                    property("border-radius", s(100))
                    property("overflow", "hidden")
                    property("flex-shrink", "0")
                    property("background", "#b4ccf6")
                }
            }) {
                if (client.avatarUrl != null) {
                    Img(src = client.avatarUrl, alt = "", attrs = {
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("object-fit", "cover")
                        }
                    })
                } else {
                    // Initials
                    Div(attrs = {
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("font-size", s(16))
                            property("font-weight", "600")
                            property("color", "#02040b")
                            property("font-family", "Urbanist, sans-serif")
                        }
                    }) {
                        val initials = (client.fullName ?: client.username)
                            .split(" ")
                            .take(2)
                            .mapNotNull { it.firstOrNull()?.uppercase() }
                            .joinToString("")
                        org.jetbrains.compose.web.dom.Text(initials)
                    }
                }
            }

            // Name + Email
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", s(4))
                }
            }) {
                SpanText(
                    client.fullName ?: client.username,
                    modifier = Modifier.bodyXlMedium().color(AppColors.Text.Secondary)
                )
                SpanText(
                    client.email,
                    modifier = Modifier.bodyMLight().color(AppColors.Text.Subtle)
                )
            }
        }

        // Bookings
        Div(attrs = { style { } }) {
            SpanText(
                client.bookingCount.toString(),
                modifier = Modifier.bodyXlMedium().color(AppColors.Text.Secondary)
            )
        }

        // Revenue
        Div(attrs = { style { } }) {
            Row(modifier = Modifier.sGap(4)) {
                SpanText(
                    "$",
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
                SpanText(
                    formatMoney(client.totalSpend),
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
            }
        }

        // Status badge
        Div(attrs = { style { } }) {
            StatusBadge(status = client.status, unpaidCount = client.unpaidCount)
        }

        // Last visit
        Div(attrs = { style { } }) {
            SpanText(
                formatLastVisit(client.lastVisitDate),
                modifier = Modifier.bodyXlMedium().color(AppColors.Text.Secondary)
            )
        }

        // Actions
        Div(attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(8))
            }
        }) {
            // Edit — 44px hit area
            Box(
                Modifier.sSize(44).cursor(Cursor.Pointer),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.Pencil,
                    modifier = Modifier.sSize(20).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )
            }

            // Delete — 44px hit area
            Box(
                Modifier.sSize(44).cursor(Cursor.Pointer),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.Trash,
                    modifier = Modifier.sSize(18).styleModifier {
                        property("filter", AppColors.IconDestructiveFilter)
                    }
                )
            }
        }
    }
}

// =============================================================================
// Status Badge
// =============================================================================

@Composable
private fun StatusBadge(status: String, unpaidCount: Int) {
    val t = LocalAppStrings.current
    val (textColor, text) = when (status.uppercase()) {
        "VIP" -> Color.hex("1E7FF5") to t.pageStatusVip
        "DEBTOR" -> Color.hex("FF4242") to t.pageStatusDebtor
        "REGULAR" -> Color.hex("7BFA6F") to t.pageStatusActive
        else -> BusinessColors.Text.OnSurfaceMuted to t.pageStatusInactive
    }

    Div(attrs = {
        style {
            property("border", "0.3px solid transparent")
            property("border-radius", s(28))
            property("padding", "${s(8)} ${s(24)}")
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", s(8))
            property("background",
                "${AppGradients.InteractiveSecondary} padding-box, " +
                "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("white-space", "nowrap")
        }
    }) {
        if (status.uppercase() == "DEBTOR" && unpaidCount > 0) {
            SpanText(
                text,
                modifier = Modifier.bodyXlMedium().color(textColor)
            )
            // Red dot separator
            Div(attrs = {
                style {
                    property("width", s(6))
                    property("height", s(6))
                    property("border-radius", s(100))
                    property("background", "#ff4242")
                    property("flex-shrink", "0")
                }
            })
            SpanText(
                unpaidCount.toString(),
                modifier = Modifier.bodyXlMedium().color(textColor)
            )
            SpanText(
                t.pageUnpaidSuffix,
                modifier = Modifier.bodyXlMedium().color(textColor)
            )
        } else {
            SpanText(
                text,
                modifier = Modifier.bodyXlMedium().color(textColor)
            )
        }
    }
}

// =============================================================================
// Pagination
// =============================================================================

@Composable
private fun ClientsPagination(
    currentPage: Int,
    totalPages: Int,
    onPageChanged: (Int) -> Unit,
) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            width(100.percent)
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "space-between")
            property("padding", "${s(12)} ${s(12)}")
        }
    }) {
        // Previous / Next buttons
        Div(attrs = {
            style {
                property("display", "flex")
                property("gap", s(8))
            }
        }) {
            // Previous
            Div(attrs = {
                style {
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(22))
                    property("padding", "${s(8)} ${s(22)} ${s(8)} ${s(14)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", s(8))
                    property("cursor", if (currentPage > 0) "pointer" else "not-allowed")
                    property("opacity", if (currentPage > 0) "1" else "0.5")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #050B18, #050B18) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("user-select", "none")
                }
                if (currentPage > 0) {
                    onClick { onPageChanged(currentPage - 1) }
                }
            }) {
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("transform", "rotate(180deg)")
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )
                SpanText(
                    t.pagePrevious,
                    modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                )
            }

            // Next
            Div(attrs = {
                style {
                    property("background-color", "#0061ff")
                    property("border-radius", s(22))
                    property("padding", "${s(8)} ${s(14)} ${s(8)} ${s(22)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", s(8))
                    property("cursor", if (currentPage < totalPages - 1) "pointer" else "not-allowed")
                    property("opacity", if (currentPage < totalPages - 1) "1" else "0.5")
                    property("user-select", "none")
                }
                if (currentPage < totalPages - 1) {
                    onClick { onPageChanged(currentPage + 1) }
                }
            }) {
                SpanText(
                    t.commonNext,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", "brightness(0) invert(1)")
                    }
                )
            }
        }

        // Page indicator
        Div(attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(8))
            }
        }) {
            SpanText(
                t.pagePageLabel,
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
            SpanText(
                "${currentPage + 1}",
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
            SpanText(
                t.pageOfLabel,
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
            SpanText(
                "$totalPages",
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
        }
    }
}

// =============================================================================
// Helpers
// =============================================================================

private fun formatMoney(value: String): String {
    return try {
        val num = value.toDouble()
        val intPart = num.toLong()
        val formatted = intPart.toString().reversed().chunked(3).joinToString(",").reversed()
        if (num != intPart.toDouble()) {
            val decimal = ((num - intPart) * 100).toLong()
            "$formatted.$decimal"
        } else {
            formatted
        }
    } catch (_: Exception) {
        value
    }
}

private fun formatLastVisit(date: String?): String {
    if (date == null) return "Never"
    return try {
        val parts = date.split("-")
        if (parts.size != 3) return date
        val year = parts[0].toInt()
        val month = parts[1].toInt()
        val day = parts[2].toInt()

        val now = js("new Date()").unsafeCast<dynamic>()
        val nowYear = (now.getFullYear() as Number).toInt()
        val nowMonth = ((now.getMonth() as Number).toInt()) + 1
        val nowDay = (now.getDate() as Number).toInt()

        val diffDays = (nowYear - year) * 365 + (nowMonth - month) * 30 + (nowDay - day)

        when {
            diffDays <= 0 -> "Today"
            diffDays == 1 -> "Yesterday"
            diffDays < 7 -> "$diffDays days ago"
            diffDays < 30 -> "${diffDays / 7} weeks ago"
            diffDays < 365 -> "${diffDays / 30} months ago"
            else -> "${diffDays / 365} years ago"
        }
    } catch (_: Exception) {
        date
    }
}
